import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Calendar,
  Edit,
  LogOut,
  User,
  Wallet,
  Phone,
} from "lucide-react";
import Modal from "../components/Modal";
import LogoLoader from "../components/LogoLoader";
import toast from "react-hot-toast";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";

const s = {
  page: {
    height: "100vh",
    background: "#faf7f0",
    fontFamily: "'Segoe UI', sans-serif",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  navbar: {
    background: "linear-gradient(135deg, #0a0a0a, #1a1a1a)",
    padding: "0 20px",
    height: "64px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 2px 20px rgba(0,0,0,0.3)",
    flexShrink: 0,
  },
  navBrand: { display: "flex", alignItems: "center", gap: "10px" },
  navCoin: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  navTitle: { color: "#fff", fontSize: "18px", fontWeight: "800", margin: 0 },
  backBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(255,255,255,0.1)",
    color: "#cbd5e1",
    border: "1px solid rgba(255,255,255,0.15)",
    padding: "8px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  content: {  padding: "32px 24px" },
  profileCard: {
    background: "linear-gradient(135deg, #0a0a0a, #1a1a1a)",
    borderRadius: "18px",
    padding: "32px",
    marginBottom: "24px",
    color: "#fff",
    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
  },
  avatar: {
    width: "60px",
    height: "60px",
    background: "linear-gradient(135deg, #C9A84C, #9A7A2C)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    fontWeight: "800",
    marginBottom: "12px",
    color: "#fff",
  },
  userName: {
    fontSize: "26px",
    fontWeight: "800",
    margin: "0 0 6px",
    color: "#fff",
  },
  userMeta: { fontSize: "14px", color: "#94a3b8", margin: "3px 0" },
  balanceBox: { textAlign: "right" },
  balLabel: {
    fontSize: "12px",
    color: "#94a3b8",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  balValue: {
    fontSize: "38px",
    fontWeight: "900",
    color: "#FBBF24",
    margin: "4px 0 0",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "12px",
    marginBottom: "24px",
  },
  infoCard: {
    background: "#fff",
    borderRadius: "12px",
    padding: "16px 20px",
    boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
    borderTop: "3px solid #C9A84C",
  },
  infoLabel: {
    fontSize: "11px",
    color: "#94a3b8",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  infoValue: {
    fontSize: "15px",
    fontWeight: "700",
    color: "#0f172a",
    marginTop: "4px",
  },
  tableCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
    overflow: "hidden",
  },
  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #f1f5f9",
  },
  tableTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0f172a",
    margin: 0,
  },
  addBtn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "linear-gradient(135deg, #C9A84C, #9A7A2C)",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    borderRadius: "9px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(201,168,76,0.3)",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "11px 14px",
    textAlign: "left",
    fontSize: "11px",
    fontWeight: "700",
    color: "#64748b",
    textTransform: "uppercase",
    background: "#f8fafc",
    letterSpacing: "0.5px",
    position: "sticky",
    top: 0,
    zIndex: 1,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 14px",
    borderBottom: "1px solid #f8fafc",
    fontSize: "13px",
    color: "#334155",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  amountBadge: {
    display: "inline-block",
    background: "#ecfdf5",
    color: "#10b981",
    borderRadius: "20px",
    padding: "3px 12px",
    fontWeight: "700",
    fontSize: "13px",
  },
  inputStyle: {
    width: "100%",
    padding: "10px 14px",
    border: "1.5px solid #e2e8f0",
    borderRadius: "9px",
    fontSize: "14px",
    background: "#f8fafc",
    boxSizing: "border-box",
    outline: "none",
    marginBottom: "14px",
  },
  inputLabel: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#374151",
    display: "block",
    marginBottom: "5px",
  },
  saveBtn: {
    width: "100%",
    padding: "12px",
    background: "linear-gradient(135deg, #C9A84C, #9A7A2C)",
    color: "#fff",
    border: "none",
    borderRadius: "9px",
    fontSize: "15px",
    fontWeight: "700",
    cursor: "pointer",
    marginTop: "4px",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    marginBottom: "24px",
  },
  summaryCard: {
    background: "#fff",
    borderRadius: "16px",
    padding: "22px 20px",
    boxShadow: "0 1px 8px rgba(0,0,0,0.07)",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  summaryIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  summaryLabel: {
    fontSize: "12px",
    fontWeight: "700",
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    margin: 0,
  },
  summaryValue: {
    fontSize: "22px",
    fontWeight: "900",
    color: "#0f172a",
    margin: "2px 0 0",
  },
  summarySub: { fontSize: "12px", color: "#94a3b8", margin: "2px 0 0" },
};

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, signOut } = useAuth();
  const isAdmin = role === "admin";
  const { adminName } = useAuth();
  const [user, setUser] = useState(null);
  const [amounts, setAmounts] = useState([]);
  const [isSavingAmount, setIsSavingAmount] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAmount, setEditingAmount] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [modalAmount, setModalAmount] = useState("");
  const [modalDate, setModalDate] = useState("");
  const [rateForDate, setRateForDate] = useState(null);
  const [gstEnabled, setGstEnabled] = useState(false);
  const [gstPercent, setGstPercent] = useState("3");
  const isSubmittingRef = useRef(false);

  const ALLOWED_ADMIN_EMAIL = 'alameengoldandsilver@gmail.com';
  const [adminEmail, setAdminEmail] = useState('');
  const [modalGram, setModalGram] = useState('');
  const [paymentType, setPaymentType] = useState('money'); // 'money' | 'direct_gram'

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAdminEmail(session?.user?.email?.toLowerCase() || '');
    });
  }, []);

  const isSuperAdmin = adminEmail === ALLOWED_ADMIN_EMAIL;

  // Init modal state when opened/closed
  useEffect(() => {
    if (isModalOpen) {
      const isDirect = editingAmount && Number(editingAmount.amount) === 0 && Number(editingAmount.gram || 0) > 0;
      setPaymentType(isDirect ? 'direct_gram' : 'money');
      setModalAmount(isDirect ? '' : (editingAmount?.amount?.toString() || ""));
      setModalDate(editingAmount?.date || new Date().toISOString().split("T")[0]);
      setGstEnabled(false);
      setGstPercent("3");
      setModalGram(editingAmount?.gram ? String(editingAmount.gram) : '');
    } else {
      setPaymentType('money');
      setModalAmount("");
      setModalDate("");
      setRateForDate(null);
      setGstEnabled(false);
      setGstPercent("3");
      setModalGram('');
    }
  }, [isModalOpen, editingAmount]);

  // Fetch metal rate whenever modal date changes
  useEffect(() => {
    if (!modalDate) { setRateForDate(null); return; }
    const fetchRate = async () => {
      const { data } = await supabase.from("metal_rates").select("*").eq("date", modalDate).maybeSingle();
      setRateForDate(data || null);
    };
    fetchRate();
  }, [modalDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data: userData } = await supabase
      .from("users")
      .select("*")
      .eq("id", id)
      .single();
    const { data: amountData } = await supabase
      .from("amounts")
      .select("*")
      .eq("user_id", id)
      .order("date", { ascending: false })
      .order("created_at", { ascending: false });
    if (userData) {
      setUser(userData);
      setAmounts(amountData || []);
    }
    setLoading(false);

    // Auto-sync users table totals on every load (fixes stale/zero totals)
    const allAmts = amountData || [];
    const newTotal = allAmts.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    const newTotalGram = allAmts.reduce((sum, r) => sum + Number(r.gram || 0), 0);
    await supabase
      .from("users")
      .update({ total_amount: newTotal, total_gram: newTotalGram })
      .eq("id", id);
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const syncTotal = async () => {
    const { data: allAmts, error: fetchErr } = await supabase
      .from("amounts")
      .select("amount, gram")
      .eq("user_id", id);
    if (fetchErr) {
      console.error("Fetch amounts error:", fetchErr.message);
      return;
    }
    const newTotal = (allAmts || []).reduce(
      (sum, r) => sum + Number(r.amount || 0),
      0,
    );
    const newTotalGram = (allAmts || []).reduce(
      (sum, r) => sum + Number(r.gram || 0),
      0,
    );
    const { error: updateErr } = await supabase
      .from("users")
      .update({ total_amount: newTotal, total_gram: newTotalGram })
      .eq("id", id);
    if (updateErr)
      console.error("Update total error:", updateErr.message);
  };

  const handleAddAmount = async (e) => {
    e.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSavingAmount(true);
    const formData = new FormData(e.target);
    const dateVal = modalDate;
    const noteVal = formData.get("note");

    let amountVal = 0;
    let gramVal = 0;

    if (paymentType === 'direct_gram' && user?.saving_type !== 'money') {
      // Direct gold/silver: customer gives physical metal, no money collected
      gramVal = parseFloat(modalGram) || 0;
      if (gramVal <= 0) {
        toast.error('Please enter a valid gram value.');
        isSubmittingRef.current = false;
        setIsSavingAmount(false);
        return;
      }
      amountVal = 0;
    } else {
      // Standard money payment
      amountVal = parseFloat(modalAmount);
      if (!amountVal || amountVal <= 0) {
        toast.error('Please enter a valid amount.');
        isSubmittingRef.current = false;
        setIsSavingAmount(false);
        return;
      }
      // Calculate gram based on saving type and rate for the selected date
      if (user?.saving_type !== "money") {
        if (rateForDate) {
          const baseRate =
            user.saving_type === "gold"
              ? Number(rateForDate.gold_rate)
              : Number(rateForDate.silver_rate);
          if (baseRate > 0) {
            const gst = gstEnabled ? (parseFloat(gstPercent) || 0) : 0;
            const effectiveRate = baseRate * (1 + gst / 100);
            gramVal = amountVal / effectiveRate;
          }
        } else if (isSuperAdmin && modalGram) {
          gramVal = parseFloat(modalGram) || 0;
        }
      }
    }

    let error;
    let insertedAmtId = null;
    const gstApplied = paymentType === 'direct_gram' ? false : gstEnabled;
    const gstPct = paymentType === 'direct_gram' ? 0 : (gstEnabled ? (parseFloat(gstPercent) || 0) : 0);
    if (editingAmount) {
      ({ error } = await supabase
        .from("amounts")
        .update({
          amount: amountVal,
          date: dateVal,
          note: noteVal,
          gram: gramVal,
          gst_applied: gstApplied,
          gst_percent: gstPct,
        })
        .eq("id", editingAmount.id));
    } else {
      const { data: inserted, error: insertErr } = await supabase.from("amounts").insert([
        {
          user_id: id,
          amount: amountVal,
          date: dateVal,
          note: noteVal,
          gram: gramVal,
          added_by: adminName,
          gst_applied: gstApplied,
          gst_percent: gstPct,
        },
      ]).select('id').single();
      error = insertErr;
      insertedAmtId = inserted?.id ?? null;
    }

    if (error) {
      isSubmittingRef.current = false;
      setIsSavingAmount(false);
      toast.error("Failed: " + error.message);
    } else {
      // Log to transaction_logs (fire and forget)
      if (editingAmount) {
        supabase.from('transaction_logs').insert([{
          event_type: 'amount_updated',
          amount_id: editingAmount.id,
          user_id: Number(id),
          member_name: user?.name || String(id),
          saving_type: user?.saving_type || 'money',
          amount: amountVal,
          gram: gramVal,
          note: noteVal,
          performed_by: adminName,
          old_data: { amount: editingAmount.amount, date: editingAmount.date, note: editingAmount.note, gram: editingAmount.gram },
          new_data: { id: editingAmount.id, user_id: String(id), amount: amountVal, date: dateVal, note: noteVal, gram: gramVal, added_by: editingAmount.added_by },
        }]).then(() => {});
      } else if (insertedAmtId) {
        supabase.from('transaction_logs').insert([{
          event_type: 'amount_added',
          amount_id: insertedAmtId,
          user_id: Number(id),
          member_name: user?.name || String(id),
          saving_type: user?.saving_type || 'money',
          amount: amountVal,
          gram: gramVal,
          note: noteVal,
          performed_by: adminName,
          new_data: { id: insertedAmtId, user_id: String(id), amount: amountVal, date: dateVal, note: noteVal, gram: gramVal, added_by: adminName },
        }]).then(() => {});
      }
      await syncTotal();
      isSubmittingRef.current = false;
      setIsSavingAmount(false);
      toast.success(editingAmount ? "Transaction updated!" : "Amount added!");
      setIsModalOpen(false);
      setEditingAmount(null);
      fetchData();
    }
  };



  if (loading) return <LogoLoader />;
  if (!user)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f1f5f9",
          color: "#ef4444",
          fontFamily: "'Segoe UI', sans-serif",
        }}
      >
        User not found.
      </div>
    );
  const computedBalance = amounts.reduce(
    (sum, a) => sum + Number(a.amount || 0),
    0,
  );
  const computedTotalGram = amounts.reduce(
    (sum, a) => sum + Number(a.gram || 0),
    0,
  );

  // Gram shown in modal preview (controlled form)
  const previewGram = (() => {
    const amt = parseFloat(modalAmount);
    if (!amt || !rateForDate || !user || user.saving_type === "money") return null;
    const baseRate =
      user.saving_type === "gold"
        ? Number(rateForDate.gold_rate)
        : Number(rateForDate.silver_rate);
    if (!baseRate) return null;
    const gst = gstEnabled ? (parseFloat(gstPercent) || 0) : 0;
    const effectiveRate = baseRate * (1 + gst / 100);
    return amt / effectiveRate;
  })();

  const filteredAmounts = amounts.filter((a) => {
    const date = new Date(a.date);
    const sd = dateFrom ? new Date(dateFrom + "T00:00:00") : null;
    const ed = dateTo ? new Date(dateTo + "T23:59:59") : null;
    return (!sd || date >= sd) && (!ed || date <= ed);
  });
  const filteredTotal = filteredAmounts.reduce(
    (sum, a) => sum + Number(a.amount || 0),
    0,
  );
  const isFiltered = dateFrom || dateTo;

  return (
    <div style={s.page}>
      {/* Navbar */}
      <nav style={s.navbar} className="gs-navbar">
        <div style={s.navBrand}>
          <div style={{ ...s.navCoin, cursor: 'pointer' }} className="gs-nav-coin" onClick={() => window.location.reload()}>
            <img
              src="/logo.jpg"
              alt="Al Ameen"
              style={{
                width: "42px",
                height: "42px",
                objectFit: "cover",
                borderRadius: "50%",
                display: "block",
              }}
            />
          </div>
          <p style={s.navTitle} className="gs-nav-title">
            Al Ameen Gold & Silver
          </p>
        </div>
        {isAdmin ? (
          <button
            onClick={() => navigate("/dashboard")}
            style={s.backBtn}
            className="gs-nav-btn"
          >
            <ArrowLeft size={15} />
            <span className="gs-btn-label">&nbsp;Dashboard</span>
          </button>
        ) : (
          <button
            onClick={async () => {
              await signOut();
              navigate("/");
            }}
            style={{
              ...s.backBtn,
              color: "#fca5a5",
              borderColor: "rgba(239,68,68,0.3)",
            }}
            className="gs-nav-btn"
          >
            <LogOut size={15} />
            <span className="gs-btn-label">&nbsp;Sign Out</span>
          </button>
        )}
      </nav>

      <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={s.content} className="gs-content">
        {/* Info Grid */}
        <div style={s.summaryGrid} className="gs-stats-3col">
          {/* Card 1 – Member Details */}
          <div style={s.summaryCard} className="gs-ud-card-0">
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  ...s.summaryIcon,
                  background: "rgba(245,158,11,0.12)",
                }}
              >
                <User size={22} color="#D97706" />
              </div>
              <div>
                <p
                  style={{
                    ...s.summaryValue,
                    fontSize: "16px",
                    marginTop: "6px",
                  }}
                >
                  {user.name}
                </p>
                <p style={s.summarySub}>ID: #{user.id}</p>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" }}>
              <p style={s.summarySub}>📞 {user.phone || "—"}</p>
              <p style={s.summarySub}>
                📍 {user.city || "—"}
                {user.pincode ? `, ${user.pincode}` : ""}
              </p>
              <p style={s.summarySub}>👨 {user.father_name || "—"}</p>
              <p style={s.summarySub}>
                📅 Joined:{" "}
                {user.created_at
                  ? new Date(user.created_at).toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })
                  : "—"}
              </p>
            </div>
          </div>

          {/* Card 2 – Savings Summary */}
          <div
            className="gs-ud-card-1"
            style={{
              ...s.summaryCard,
              background: "linear-gradient(135deg,#0f172a,#1e3a5f)",
              color: "#fff",
            }}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              <div
                style={{
                  ...s.summaryIcon,
                  background: "rgba(251,191,36,0.15)",
                }}
              >
                <Wallet size={22} color="#FBBF24" />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <p style={{ ...s.summarySub, color: "#94a3b8" }}>
                  {user.saving_type === "gold"
                    ? "Gold Saving"
                    : user.saving_type === "silver"
                    ? "Silver Saving"
                    : "Money Saving"}
                </p>
                <p style={{ ...s.summaryValue, color: "#FBBF24", fontSize: "23" }}>
                  ₹{computedBalance.toLocaleString("en-IN")}
                </p>
                {user.saving_type !== "money" && (
                  <p style={{ margin: "2px 0 0", fontSize: "14px", fontWeight: "700", color: "#a5f3fc" }}>
                    {computedTotalGram.toFixed(3)} g {user.saving_type === "gold" ? "Gold" : "Silver"}
                  </p>
                )}
              </div>
            </div>
            <div>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  marginTop: "10px",
                }}
              >
                <div
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    flex: 1,
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: "800",
                      color: "#fff",
                    }}
                  >
                    {amounts.length}
                  </p>
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: "11px",
                      color: "#94a3b8",
                      fontWeight: "600",
                    }}
                  >
                    Payments
                  </p>
                </div>
                <div
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    borderRadius: "8px",
                    padding: "8px 14px",
                    flex: 1,
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      fontWeight: "800",
                      color: "#fff",
                    }}
                  >
                    {amounts.length > 0
                      ? new Date(
                          amounts[amounts.length - 1].date,
                        ).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                        })
                      : "—"}
                  </p>
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: "11px",
                      color: "#94a3b8",
                      fontWeight: "600",
                    }}
                  >
                    First Pay
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3 – Contact / Enquiry */}
          {!isAdmin && (
            <div className="gs-ud-card-2" style={{ ...s.summaryCard, border: "1.5px solid #fde68a" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    ...s.summaryIcon,
                    background: "rgba(245,158,11,0.1)",
                  }}
                >
                  <Phone size={22} color="#D97706" />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                <p style={s.summaryLabel}>Enquiry & Contact</p>
                <p
                  style={{
                    ...s.summaryValue,
                    fontSize: "15px",
                    color: "#0f172a",
                    marginTop: "6px",
                  }}
                >
                  Al Ameen Gold & Silver
                </p>
                </div>
              </div>
              <div>
                <p style={s.summarySub}>
                  For any queries about your account or savings plan, reach out
                  to us:
                </p>
                <a
                  href="tel:+918825888717"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginTop: "10px",
                    textDecoration: "none",
                    color: "#D97706",
                    fontWeight: "700",
                    fontSize: "14px",
                  }}
                >
                  <Phone size={14} /> 8825888717
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Transactions Table */}
        <div style={s.tableCard} className="gs-ud-table">
          <div style={s.tableHeader} className="gs-tbl-hdr">
            <div>
              <h3 style={s.tableTitle}>Payment History</h3>
              {isFiltered && (
                <p
                  style={{
                    margin: "4px 0 0",
                    fontSize: "12px",
                    color: "#64748b",
                  }}
                >
                  {filteredAmounts.length} of {amounts.length} records
                  &nbsp;•&nbsp; ₹{filteredTotal.toLocaleString("en-IN")}{" "}
                  filtered
                </p>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                flexWrap: "wrap",
              }}
              className="gs-filters-wrap"
            >
              {/* Date From */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
                className="gs-date-group"
              >
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: "700",
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                  }}
                >
                  From
                </span>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    padding: "7px 10px",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "9px",
                    fontSize: "13px",
                    color: "#334155",
                    background: "#f8fafc",
                    outline: "none",
                  }}
                />
              </div>
              {/* Date To */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
                className="gs-date-group"
              >
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: "700",
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                  }}
                >
                  To
                </span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    padding: "7px 10px",
                    border: "1.5px solid #e2e8f0",
                    borderRadius: "9px",
                    fontSize: "13px",
                    color: "#334155",
                    background: "#f8fafc",
                    outline: "none",
                  }}
                />
              </div>
              {/* Clear filters */}
              {isFiltered && (
                <button
                  onClick={() => {
                    setDateFrom("");
                    setDateTo("");
                  }}
                  style={{
                    alignSelf: "flex-end",
                    padding: "7px 12px",
                    background: "#fef2f2",
                    color: "#ef4444",
                    border: "1.5px solid #fecaca",
                    borderRadius: "9px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => setIsModalOpen(true)}
                  style={{ ...s.addBtn, alignSelf: "flex-end" }}
                >
                  <Plus size={16} /> Add Amount
                </button>
              )}
            </div>
          </div>
          <div className="gs-table-scroll" style={{ overflowX: "auto", overflowY: "auto", maxHeight: "520px" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Date & Time</th>
                  <th style={s.th}>Note</th>
                  <th style={s.th}>Added By</th>
                  {user?.saving_type !== "money" && (
                    <th style={{ ...s.th, textAlign: "center" }}>Gram</th>
                  )}
                  {user?.saving_type !== "money" && (
                    <th style={{ ...s.th, textAlign: "center" }}>GST</th>
                  )}
                  {user?.saving_type !== "money" && (
                    <th style={{ ...s.th, textAlign: "center" }}>GST %</th>
                  )}
                  <th style={{ ...s.th, textAlign: "center" }}>Amount</th>
                  {isSuperAdmin && (
                    <th style={{ ...s.th, textAlign: "center" }}>Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredAmounts.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isSuperAdmin ? (user?.saving_type !== "money" ? 8 : 5) : (user?.saving_type !== "money" ? 7 : 4)}
                      style={{
                        ...s.td,
                        textAlign: "center",
                        color: "#94a3b8",
                        padding: "40px",
                      }}
                    >
                      {isFiltered
                        ? "No transactions match your filter."
                        : "No transactions yet."}
                    </td>
                  </tr>
                ) : (
                  filteredAmounts.map((item, i) => (
                    <tr
                      key={item.id}
                      style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}
                    >
                      <td style={s.td}>
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Calendar size={13} style={{ color: "#94a3b8" }} />
                          <span>
                            {new Date(item.date).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                            {item.created_at && (
                              <span style={{ fontSize: "11px", color: "#94a3b8", marginLeft: "5px" }}>
                                {new Date(item.created_at).toLocaleTimeString("en-IN", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                })}
                              </span>
                            )}
                          </span>
                        </span>
                      </td>
                      <td
                        style={{
                          ...s.td,
                          color: "#64748b",
                          fontStyle: "italic",
                        }}
                      >
                        {item.note || "—"}
                      </td>
                      <td style={s.td}>
                        <span style={{ fontSize: "13px", color: "#64748b" }}>
                          {item.added_by || "—"}
                        </span>
                      </td>
                      {user?.saving_type !== "money" && (
                        <td style={{ ...s.td, textAlign: "center" }}>
                          <span style={{ display: "inline-block", background: user.saving_type === "gold" ? "#fef3c7" : "#f1f5f9", color: user.saving_type === "gold" ? "#D97706" : "#475569", borderRadius: "20px", padding: "3px 10px", fontWeight: "700", fontSize: "13px" }}>
                            {item.gram ? `${Number(item.gram).toFixed(3)}g` : "—"}
                          </span>
                        </td>
                      )}
                      {user?.saving_type !== "money" && (
                        <td style={{ ...s.td, textAlign: "center" }}>
                          {item.gst_applied
                            ? <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", background: "#dcfce7", color: "#16a34a", borderRadius: "50%", border: "1.5px solid #bbf7d0", fontSize: "11px", fontWeight: "900" }}>✓</span>
                            : <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", background: "#fef2f2", color: "#ef4444", borderRadius: "50%", border: "1.5px solid #fecaca", fontSize: "11px", fontWeight: "900" }}>✕</span>
                          }
                        </td>
                      )}
                      {user?.saving_type !== "money" && (
                        <td style={{ ...s.td, textAlign: "center" }}>
                          {item.gst_applied && item.gst_percent
                            ? <span style={{ display: "inline-block", background: "#fef9ec", color: "#C9A84C", borderRadius: "20px", padding: "3px 10px", fontWeight: "700", fontSize: "12px", border: "1px solid #f5d87e" }}>{item.gst_percent}%</span>
                            : <span style={{ display: "inline-block", background: "#f1f5f9", color: "#64748b", borderRadius: "20px", padding: "3px 10px", fontWeight: "700", fontSize: "12px" }}>0%</span>
                          }
                        </td>
                      )}
                      <td style={{ ...s.td, textAlign: "center" }}>
                        {Number(item.amount) === 0 && Number(item.gram || 0) > 0 ? (
                          <span style={{ display: 'inline-block', background: user.saving_type === 'gold' ? '#fef3c7' : '#f1f5f9', color: user.saving_type === 'gold' ? '#92400e' : '#334155', borderRadius: '20px', padding: '3px 12px', fontWeight: '700', fontSize: '12px', border: `1px solid ${user.saving_type === 'gold' ? '#fde68a' : '#e2e8f0'}` }}>
                            {user.saving_type === 'gold' ? '🥇' : '🥈'} Direct
                          </span>
                        ) : (
                          <span style={s.amountBadge}>
                            ₹{Number(item.amount).toLocaleString()}
                          </span>
                        )}
                      </td>
                      {isSuperAdmin && (
                        <td style={{ ...s.td, textAlign: "center" }}>
                          <span style={{ display: "inline-flex", gap: "8px" }}>
                            <button
                              title="Edit"
                              style={{
                                color: "#10b981",
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setEditingAmount(item);
                                setIsModalOpen(true);
                              }}
                            >
                              <Edit size={15} />
                            </button>
                          </span>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', fontSize: '12px', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
            <span>{filteredAmounts.length} {filteredAmounts.length === 1 ? 'Record shown' : 'Records shown'}</span>
            {(dateFrom || dateTo) && <span style={{ color: '#C9A84C', fontWeight: '700' }}>⚡ Filters applied</span>}
          </div>
        </div>
      </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAmount(null);
        }}
        title={editingAmount ? "Edit Transaction" : "Add Transaction"}
      >
        <form onSubmit={handleAddAmount}>

          {/* ── Payment type toggle (gold/silver members only) ── */}
          {user?.saving_type !== 'money' && (
            <div style={{ display: 'flex', gap: '6px', marginBottom: '18px', background: '#f8fafc', borderRadius: '12px', padding: '5px' }}>
              <button
                type="button"
                onClick={() => { setPaymentType('money'); setModalGram(''); }}
                style={{
                  flex: 1, padding: '9px 10px', borderRadius: '9px', border: 'none',
                  fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s',
                  background: paymentType === 'money' ? 'linear-gradient(135deg,#C9A84C,#9A7A2C)' : 'transparent',
                  color: paymentType === 'money' ? '#fff' : '#64748b',
                  boxShadow: paymentType === 'money' ? '0 2px 8px rgba(201,168,76,0.35)' : 'none',
                }}
              >
                💰 Money Payment
              </button>
              <button
                type="button"
                onClick={() => { setPaymentType('direct_gram'); setModalAmount(''); }}
                style={{
                  flex: 1, padding: '9px 10px', borderRadius: '9px', border: 'none',
                  fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s',
                  background: paymentType === 'direct_gram'
                    ? (user.saving_type === 'gold' ? 'linear-gradient(135deg,#92400e,#B45309)' : 'linear-gradient(135deg,#334155,#475569)')
                    : 'transparent',
                  color: paymentType === 'direct_gram' ? '#fff' : '#64748b',
                  boxShadow: paymentType === 'direct_gram' ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
                }}
              >
                {user.saving_type === 'gold' ? '🥇 Direct Gold' : '🥈 Direct Silver'}
              </button>
            </div>
          )}

          {/* ══ DIRECT GRAM MODE ══ */}
          {paymentType === 'direct_gram' && user?.saving_type !== 'money' ? (
            <>
              {/* Info banner */}
              <div style={{ marginBottom: '14px', padding: '10px 14px', background: user.saving_type === 'gold' ? '#fef9ee' : '#f8fafc', borderRadius: '10px', border: `1px solid ${user.saving_type === 'gold' ? '#fde68a' : '#e2e8f0'}` }}>
                <p style={{ margin: 0, fontSize: '13px', color: user.saving_type === 'gold' ? '#92400e' : '#475569', fontWeight: '600' }}>
                  {user.saving_type === 'gold' ? '🥇' : '🥈'} Customer gave {user.saving_type} physically — no money collected. Amount will be stored as ₹0.
                </p>
              </div>

              {/* Date */}
              <label style={s.inputLabel}>Date</label>
              <input
                name="date"
                type="date"
                required
                value={modalDate}
                onChange={(e) => isSuperAdmin ? setModalDate(e.target.value) : undefined}
                readOnly={!isSuperAdmin}
                style={{ ...s.inputStyle, ...(!isSuperAdmin ? { background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' } : {}) }}
              />
              {!isSuperAdmin && (
                <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '-10px', marginBottom: '14px' }}>Date is locked to today.</p>
              )}

              {/* Gram input */}
              <label style={s.inputLabel}>
                {user.saving_type === 'gold' ? '🥇 Gold' : '🥈 Silver'} Gram Received
              </label>
              <input
                type="number"
                step="0.001"
                min="0.001"
                placeholder="e.g. 5.000"
                value={modalGram}
                onChange={e => setModalGram(e.target.value)}
                required
                style={{ ...s.inputStyle, borderColor: user.saving_type === 'gold' ? '#C9A84C' : '#94a3b8' }}
              />

              {/* Equivalent value reference */}
              {rateForDate && parseFloat(modalGram) > 0 && (
                <div style={{ marginBottom: '14px', padding: '10px 14px', background: '#f0fdf4', borderRadius: '9px', border: '1px solid #bbf7d0' }}>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Equivalent value at today's rate: </span>
                  <span style={{ fontSize: '15px', fontWeight: '800', color: '#059669' }}>
                    ₹{((parseFloat(modalGram) || 0) * Number(user.saving_type === 'gold' ? rateForDate.gold_rate : rateForDate.silver_rate)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}> (reference only — ₹0 stored)</span>
                </div>
              )}

              {/* Note */}
              <label style={s.inputLabel}>Note (optional)</label>
              <input
                name="note"
                type="text"
                placeholder={`e.g. Customer gave ${parseFloat(modalGram) > 0 ? parseFloat(modalGram).toFixed(3) + 'g' : ''} ${user.saving_type} directly`}
                defaultValue={editingAmount?.note || ''}
                style={s.inputStyle}
              />

              <button
                type="submit"
                disabled={isSavingAmount || !(parseFloat(modalGram) > 0)}
                style={{
                  ...s.saveBtn,
                  opacity: (isSavingAmount || !(parseFloat(modalGram) > 0)) ? 0.5 : 1,
                  cursor: (isSavingAmount || !(parseFloat(modalGram) > 0)) ? 'not-allowed' : 'pointer',
                  background: user.saving_type === 'gold' ? 'linear-gradient(135deg,#C9A84C,#9A7A2C)' : 'linear-gradient(135deg,#334155,#475569)',
                }}
              >
                {isSavingAmount ? 'Saving…' : editingAmount ? 'Update Entry' : `Save Direct ${user.saving_type === 'gold' ? 'Gold' : 'Silver'} Entry`}
              </button>
            </>
          ) : (

          /* ══ MONEY PAYMENT MODE (existing) ══ */
          <>
            <label style={s.inputLabel}>Amount (₹)</label>
            <input
              name="amount"
              type="number"
              step="0.01"
              placeholder="e.g. 500"
              required
              value={modalAmount}
              onChange={(e) => setModalAmount(e.target.value)}
              style={s.inputStyle}
            />
            <label style={s.inputLabel}>Date</label>
            <input
              name="date"
              type="date"
              required
              value={modalDate}
              onChange={(e) => isSuperAdmin ? setModalDate(e.target.value) : undefined}
              readOnly={!isSuperAdmin}
              style={{ ...s.inputStyle, ...(!isSuperAdmin ? { background: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' } : {}) }}
            />
            {!isSuperAdmin && (
              <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '-10px', marginBottom: '14px' }}>
                Date is locked to today's date.
              </p>
            )}
            {/* Gram calculation preview for gold/silver saving types */}
            {user?.saving_type !== "money" && (
              <>
                {/* GST Toggle */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                  <span style={{ fontSize: "13px", fontWeight: "700", color: "#374151" }}>Apply GST</span>
                  <div
                    onClick={() => setGstEnabled(v => !v)}
                    style={{
                      width: "42px", height: "22px", borderRadius: "11px", cursor: "pointer",
                      background: gstEnabled ? "#C9A84C" : "#cbd5e1",
                      position: "relative", transition: "background 0.2s",
                      flexShrink: 0,
                    }}
                  >
                    <div style={{
                      position: "absolute", top: "3px",
                      left: gstEnabled ? "22px" : "3px",
                      width: "16px", height: "16px",
                      borderRadius: "50%", background: "#fff",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      transition: "left 0.2s",
                    }} />
                  </div>
                </div>
                {gstEnabled && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                    <label style={{ ...s.inputLabel, margin: 0, whiteSpace: "nowrap" }}>GST %</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={gstPercent}
                      onChange={e => setGstPercent(e.target.value)}
                      style={{ ...s.inputStyle, margin: 0, width: "100px" }}
                      placeholder="e.g. 3"
                    />
                  </div>
                )}
                <div style={{ marginBottom: "14px", padding: "12px 14px", background: rateForDate ? "#f0fdf4" : "#fef2f2", borderRadius: "9px", border: `1px solid ${rateForDate ? "#bbf7d0" : "#fecaca"}` }}>
                  <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
                    {user.saving_type === "gold" ? "🥇 Gold" : "🥈 Silver"} Gram Calculation{gstEnabled ? ` (GST ${gstPercent}%)` : ""}
                  </div>
                  {rateForDate ? (
                    <>
                      <div style={{ fontSize: "13px", color: "#64748b" }}>
                        Base rate: ₹{Number(user.saving_type === "gold" ? rateForDate.gold_rate : rateForDate.silver_rate).toLocaleString("en-IN")}/g
                        {gstEnabled && parseFloat(gstPercent) > 0 && (
                          <span style={{ marginLeft: "8px", color: "#f59e0b", fontWeight: "700" }}>
                            → effective ₹{(Number(user.saving_type === "gold" ? rateForDate.gold_rate : rateForDate.silver_rate) * (1 + (parseFloat(gstPercent) || 0) / 100)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}/g
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "18px", fontWeight: "900", color: "#059669", marginTop: "4px" }}>
                        {previewGram !== null ? `= ${previewGram.toFixed(3)} g` : "Enter an amount above"}
                      </div>
                    </>
                  ) : (
                    <div>
                      <div style={{ fontSize: "13px", color: "#dc2626" }}>
                        ⚠️ No metal rate found for {modalDate || "selected date"}.
                        {isSuperAdmin ? " Enter gram manually below (old record)." : " Gram will be saved as 0."}
                      </div>
                      {isSuperAdmin && (
                        <div style={{ marginTop: "10px" }}>
                          <label style={{ ...s.inputLabel, color: '#92400e' }}>Gram (Manual Entry for Old Record)</label>
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            placeholder="e.g. 2.500"
                            value={modalGram}
                            onChange={e => setModalGram(e.target.value)}
                            style={{ ...s.inputStyle, borderColor: '#C9A84C', marginBottom: 0 }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
            <label style={s.inputLabel}>Note (optional)</label>
            <input
              name="note"
              type="text"
              placeholder="e.g. Monthly instalment"
              defaultValue={editingAmount?.note || ""}
              style={s.inputStyle}
            />
            {(() => {
              const blocked = (!isSuperAdmin && user?.saving_type !== "money" && !rateForDate) || isSavingAmount;
              return (
                <button
                  type="submit"
                  disabled={blocked}
                  style={{ ...s.saveBtn, opacity: blocked ? 0.5 : 1, cursor: blocked ? "not-allowed" : "pointer" }}
                  title={!isSuperAdmin && user?.saving_type !== "money" && !rateForDate ? "Set today's metal rate first before adding an amount" : ""}
                >
                  {isSavingAmount ? "Saving…" : editingAmount ? "Update Transaction" : "Save Transaction"}
                </button>
              );
            })()}
          </>
          )}
        </form>
      </Modal>
    </div>
  );
}
